import { ChartBarIcon } from "@phosphor-icons/react";
import { PageHeader } from "@bednbody/ui/components/page-header";
import { StatTile } from "@bednbody/ui/components/stat-tile";
import { NativeSelectOption } from "@bednbody/ui/components/native-select";
import { NativeSelect } from "@bednbody/ui/components/native-select";
import { Input } from "@bednbody/ui/components/input";
import { SurfaceCard } from "@bednbody/ui/components/surface-card";
import { Callout } from "@bednbody/ui/components/callout";
import { useState } from "react";
import {
  AdminDataTable,
  AdminTableIdentity,
  AdminTableRowMenu,
} from "@bednbody/ui/components/admin-data-table";
import { Badge } from "@bednbody/ui/components/badge";
import { Button } from "@bednbody/ui/components/button";
import type { ColumnDef } from "@tanstack/react-table";
import {
  useAllDocuments,
  useUploadDocument,
  useGetDocumentUrl,
  type StaffDocument,
} from "./documents.hooks";

const TYPE_LABELS: Record<string, string> = {
  health_cert: "Health Certificate",
  gov_id: "Government ID",
  contract: "Employment Contract",
  nbi: "NBI Clearance",
};

export function DocumentsPage() {
  const { data: documents, isLoading, isError, error } = useAllDocuments();
  const uploadDocument = useUploadDocument();
  const getDocumentUrl = useGetDocumentUrl();
  const [showUpload, setShowUpload] = useState(false);

  const handleUpload = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const staffId = formData.get("staffId") as string;
    const file = formData.get("file") as File;
    const type = formData.get("type") as string;
    const expiryDate = formData.get("expiryDate") as string | null;

    if (!file || !staffId || !type) return;

    await uploadDocument.mutateAsync({ staffId, file, type, expiryDate: expiryDate || null });
    setShowUpload(false);
  };

  const handleViewDocument = async (filePath: string) => {
    const { url } = await getDocumentUrl.mutateAsync(filePath);
    window.open(url, "_blank");
  };

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading documents…</p>;
  }

  if (isError) {
    return (
      <Callout tone="warning">
        <p className="font-medium text-warning">Staff Documents isn't built yet</p>
        <p className="mt-1 text-muted-foreground">{(error as Error).message}</p>
      </Callout>
    );
  }

  const expiringSoon = documents?.filter((d) => d.status === "expiring_soon").length || 0;
  const expired = documents?.filter((d) => d.status === "expired").length || 0;
  const columns: ColumnDef<StaffDocument, unknown>[] = [
    {
      id: "staff",
      accessorFn: (doc) => doc.staffName,
      header: "Staff",
      cell: ({ row }) => (
        <AdminTableIdentity
          title={row.original.staffName}
          subtitle={TYPE_LABELS[row.original.type] ?? row.original.type}
          fallback={row.original.staffName.slice(0, 2).toUpperCase()}
        />
      ),
    },
    { id: "type", accessorFn: (doc) => TYPE_LABELS[doc.type] ?? doc.type, header: "Document type" },
    { id: "name", accessorFn: (doc) => doc.name, header: "Name" },
    {
      id: "expiry",
      accessorFn: (doc) => doc.expiryDate ?? "",
      header: "Expiry date",
      cell: ({ row }) => row.original.expiryDate ?? "—",
    },
    {
      id: "status",
      accessorFn: (doc) => doc.status,
      header: "Status",
      cell: ({ row }) => (
        <Badge
          variant={
            row.original.status === "valid"
              ? "success"
              : row.original.status === "expired"
                ? "destructive"
                : "warning"
          }
          className="capitalize"
        >
          {row.original.status.replaceAll("_", " ")}
        </Badge>
      ),
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      cell: ({ row }) => (
        <AdminTableRowMenu
          label={row.original.name}
          actions={
            row.original.file_path
              ? [
                  {
                    label: "View document",
                    onSelect: () => void handleViewDocument(row.original.file_path!),
                  },
                ]
              : [{ label: "File unavailable", onSelect: () => undefined, disabled: true }]
          }
        />
      ),
    },
  ];

  return (
    <div className="min-w-0 space-y-8">
      <PageHeader
        description="Manage and track staff documents with expiry alerts."
        title="Staff Documents"
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatTile layout="compact" label="Total Documents" icon={ChartBarIcon}>
          <p className="text-2xl font-semibold text-primary-dark">{documents?.length || 0}</p>
        </StatTile>
        <StatTile layout="compact" label="Expiring Soon" icon={ChartBarIcon} tone="warning">
          <p className="text-2xl font-semibold text-warning">{expiringSoon}</p>
        </StatTile>
        <StatTile layout="compact" label="Expired" icon={ChartBarIcon} tone="danger">
          <p className="text-2xl font-semibold text-destructive">{expired}</p>
        </StatTile>
      </div>

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-primary-dark">All Documents</h2>
        <Button onClick={() => setShowUpload(true)}>Upload Document</Button>
      </div>

      {showUpload ? (
        <SurfaceCard>
          <h3 className="mb-4 text-sm font-semibold text-primary-dark">Upload Document</h3>
          <form onSubmit={handleUpload} className="space-y-4">
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Staff ID</label>
              <Input
                name="staffId"
                type="text"
                required
                className="w-full"
                placeholder="Enter staff ID"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Document Type</label>
              <NativeSelect name="type" required className="w-full">
                <NativeSelectOption value="">Select type</NativeSelectOption>
                <NativeSelectOption value="health_cert">Health Certificate</NativeSelectOption>
                <NativeSelectOption value="gov_id">Government ID</NativeSelectOption>
                <NativeSelectOption value="contract">Employment Contract</NativeSelectOption>
                <NativeSelectOption value="nbi">NBI Clearance</NativeSelectOption>
              </NativeSelect>
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">File</label>
              <Input name="file" type="file" required className="w-full" />
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">
                Expiry Date (optional)
              </label>
              <Input name="expiryDate" type="date" className="w-full" />
            </div>
            <div className="flex gap-2">
              <Button variant="default" size="sm" type="submit">
                Upload
              </Button>
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={() => setShowUpload(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </SurfaceCard>
      ) : null}

      <AdminDataTable
        presentation="standalone"
        data={documents ?? []}
        columns={columns}
        getRowId={(doc) => doc.id}
        searchText={(doc) => [doc.staffName, doc.name, TYPE_LABELS[doc.type], doc.status].join(" ")}
        searchPlaceholder="Search staff or document…"
        facets={[
          {
            id: "status",
            label: "Status",
            options: [
              { value: "valid", label: "Valid" },
              { value: "expiring_soon", label: "Expiring soon" },
              { value: "expired", label: "Expired" },
            ],
            getValue: (doc) => doc.status,
          },
        ]}
        initialSorting={[{ id: "expiry", desc: false }]}
        exportFilename="staff-documents"
        exportColumns={[
          { header: "Staff", value: (doc) => doc.staffName },
          { header: "Type", value: (doc) => doc.type },
          { header: "Name", value: (doc) => doc.name },
          { header: "Expiry", value: (doc) => doc.expiryDate },
          { header: "Status", value: (doc) => doc.status },
        ]}
      />
    </div>
  );
}
